/**
 * ChatbotPanel - Support chat interface
 *
 * Improved styling to match app-wide design system:
 * - Uses CSS variables for theming
 * - Consistent with Tailwind UI components
 * - Professional appearance
 * - Smooth animations
 */

import React, { useState, useEffect, useRef } from 'react';

export interface ChatMessage {
  text: string;
  isUser: boolean;
  timestamp?: Date;
}

export interface ChatbotPanelProps {
  /** Whether the chatbot panel is open */
  isOpen: boolean;
  /** Callback when panel should close */
  onClose: () => void;
  /** Initial message to prepopulate */
  initialMessage?: string;
  /** Custom welcome messages */
  welcomeMessages?: string[];
  /** Custom send handler */
  onSendMessage?: (message: string) => void;
  /** Panel position */
  position?: 'bottom-right' | 'bottom-left';
  /** Custom className */
  className?: string;
}

export function ChatbotPanel({
  isOpen,
  onClose,
  initialMessage,
  welcomeMessages = [
    "Hello! How can I help you today?",
    "I'm here to assist with account setup, trading questions, or technical support."
  ],
  onSendMessage,
  position = 'bottom-right',
  className = '',
}: ChatbotPanelProps) {
  const [message, setMessage] = useState(initialMessage || '');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Initialize welcome messages when opened
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages(welcomeMessages.map(text => ({ text, isUser: false, timestamp: new Date() })));
    }
  }, [isOpen, welcomeMessages]);

  // Set initial message when provided
  useEffect(() => {
    if (initialMessage && isOpen) {
      setMessage(initialMessage);
      // Focus input after a brief delay
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [initialMessage, isOpen]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  if (!isOpen) return null;

  const handleSend = () => {
    const trimmedMessage = message.trim();
    if (!trimmedMessage) return;

    // Add user message
    setMessages(prev => [...prev, { text: trimmedMessage, isUser: true, timestamp: new Date() }]);

    // Custom handler or simulate bot response
    if (onSendMessage) {
      onSendMessage(trimmedMessage);
    } else {
      setTimeout(() => {
        setMessages(prev => [...prev, {
          text: "Thanks for your message. A support agent will assist you shortly.",
          isUser: false,
          timestamp: new Date()
        }]);
      }, 1000);
    }

    setMessage('');
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const positionClasses = position === 'bottom-right'
    ? 'bottom-4 right-4'
    : 'bottom-4 left-4';

  return (
    <div
      className={`fixed ${positionClasses} w-96 h-[500px] animate-slide-up z-[9999] ${className}`}
    >
      {/* Chat window */}
      <div
        className="flex flex-col h-full rounded-2xl shadow-2xl overflow-hidden bg-card border border-input"
      >
        {/* Header */}
        <div
          className="flex items-center justify-between p-4 border-b border-input"
        >
          <div className="flex items-center space-x-3">
            <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
            <h3
              className="font-semibold text-foreground"
            >
              Support Assistant
            </h3>
          </div>
          <button
            onClick={onClose}
            className="transition-colors text-muted-foreground hover:text-primary"
            aria-label="Close chat"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Messages area */}
        <div
          className="flex-1 overflow-y-auto p-4 space-y-3 messages-scroll"
        >
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`flex ${msg.isUser ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] px-4 py-2 rounded-lg text-sm ${msg.isUser ? 'bg-primary text-btn-primary-text' : 'bg-secondary text-muted-foreground'}`}
              >
                {msg.text}
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {/* Input area */}
        <div
          className="p-4 border-t border-border"
        >
          <div className="flex space-x-2">
            <input
              ref={inputRef}
              type="text"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your message..."
              className="flex-1 px-4 py-2 rounded-lg transition-all focus:outline-none focus:ring-2 chatbot-input bg-secondary border border-input text-foreground"
            />
            <button
              onClick={handleSend}
              disabled={!message.trim()}
              className="px-4 py-2 rounded-lg transition-all disabled:opacity-disabled disabled:cursor-not-allowed bg-primary text-btn-primary-text hover:bg-btn-primary-hover"
              aria-label="Send message"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .messages-scroll {
          scrollbar-width: thin;
          scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
        }
        .messages-scroll::-webkit-scrollbar {
          width: 8px;
        }
        .messages-scroll::-webkit-scrollbar-thumb {
          background-color: rgba(255, 255, 255, 0.2);
          border-radius: 4px;
        }
        .messages-scroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .chatbot-input::placeholder {
          color: var(--text-muted-foreground, rgba(255, 255, 255, 0.4));
        }
      `}</style>
    </div>
  );
}

export default ChatbotPanel;
